import { SupabaseService } from "../../../server/supabase/base-supabase-service";

import type { UUID } from "../../../utils/uuid";

type ContextValueKey =
  | "industry"
  | "company_description"
  | "company_values"
  | "company_team_reference";

export class SupabaseContextService extends SupabaseService<"context"> {
  table = "context" as const;

  async getValues<K extends ContextValueKey>(
    account_id: UUID,
    keys: readonly K[],
  ): Promise<Record<K, string | null>> {
    const { data, error } = this.log_errors(
      await this.client
        .from(this.table)
        .select("key,result,created_at")
        .eq("account_id", account_id)
        .in("key", [...keys])
        .order("created_at", { ascending: false })
        .overrideTypes<{ key: K; result: string | null }[], { merge: false }>(),
    );

    if (error) {
      throw error;
    }

    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    const values = Object.fromEntries(keys.map((key) => [key, null])) as Record<K, string | null>;

    data.forEach(({ key, result }) => {
      if (values[key] === null) values[key] = result;
    });

    return values;
  }

  async getValue(account_id: UUID, key: ContextValueKey): Promise<string | null> {
    const values = await this.getValues(account_id, [key]);
    return values[key];
  }

  async setValues(values: Record<ContextValueKey, string | null>) {
    return this.log_errors(
      await this.client.from(this.table).insert(
        Object.entries(values).map(([key, result]) => ({
          // oxlint-disable-next-line typescript/no-unsafe-type-assertion
          key: key as ContextValueKey,
          result,
        })),
      ),
    );
  }
}
